import type { Animal, Breed, Listing, Search } from 'shared/@generated/models';
import type { Coordinate, Paginator } from 'shared/@types';
import type { RequestClient } from 'shared/library/api-client';
import { apiRoute } from 'shared/library/api-route';
import type { ListingsFilter } from 'shared/state/listings-filter';

export interface SearchData extends Omit<ListingsFilter, 'page' | 'sortBy'> {
  name: string;
  notify: boolean;
}

export function getSavedSearchBreedsForAnimal(
  selected: SearchData['breeds'],
  animal: Animal | null,
  breeds: Pick<Breed, 'id' | 'animal'>[],
) {
  return (selected ?? []).filter((value) => {
    if (!animal || typeof value === 'string') return true;

    const breed = breeds.find((breed) => breed.id === value);
    return !breed || breed.animal === animal;
  });
}

export function normalizeSearchData(
  initialData?: Partial<SearchData>,
  legacyLocation?: Coordinate<number | null>,
): SearchData {
  const initialLocation = {
    latitude: initialData?.latitude ?? null,
    longitude: initialData?.longitude ?? null,
  };
  const location = hasSavedSearchLocation(initialLocation)
    ? initialLocation
    : legacyLocation && hasSavedSearchLocation(legacyLocation)
      ? legacyLocation
      : null;
  const maxDistanceInKm = initialData?.maxDistanceInKm ?? null;
  const hasDistance =
    typeof maxDistanceInKm === 'number' &&
    Number.isFinite(maxDistanceInKm) &&
    maxDistanceInKm >= 1 &&
    maxDistanceInKm <= 200;

  return {
    name: initialData?.name ?? '',
    breeds: initialData?.breeds ?? [],
    country: initialData?.country ?? null,
    animal: initialData?.animal ?? null,
    breedType: initialData?.breedType ?? null,
    gender: initialData?.gender ?? null,
    petAge: initialData?.petAge ?? null,
    onlyBreeders: initialData?.onlyBreeders ?? null,
    maxPrice: initialData?.maxPrice === 50000 ? null : (initialData?.maxPrice ?? null),
    maxDistanceInKm: location && hasDistance ? maxDistanceInKm : null,
    latitude: location && hasDistance ? location.latitude : null,
    longitude: location && hasDistance ? location.longitude : null,
    notify: initialData?.notify ?? true,
  };
}

export function normalizeSavedSearchLocationChange(
  currentLocation: Coordinate<number | null>,
  nextLocation: Coordinate<number | null> & { maxDistanceInKm: number | null },
) {
  if (!hasSavedSearchLocation(nextLocation)) {
    return { latitude: null, longitude: null, maxDistanceInKm: null };
  }

  return {
    latitude: nextLocation.latitude,
    longitude: nextLocation.longitude,
    maxDistanceInKm:
      nextLocation.maxDistanceInKm ?? (hasSavedSearchLocation(currentLocation) ? 200 : 50),
  };
}

export function hasSavedSearchLocation(
  location: Coordinate<number | null>,
): location is Coordinate<number> {
  return (
    typeof location.latitude === 'number' &&
    Number.isFinite(location.latitude) &&
    location.latitude >= -90 &&
    location.latitude <= 90 &&
    typeof location.longitude === 'number' &&
    Number.isFinite(location.longitude) &&
    location.longitude >= -180 &&
    location.longitude <= 180
  );
}

export function searchToSearchData(search: Search): SearchData {
  return {
    name: search.name,
    breeds: (search.search_breeds ?? []).map((breed) => breed.breed_id ?? breed.query!),
    country: search.country,
    animal: search.animal,
    breedType: search.breed_type,
    gender: search.gender,
    petAge: search.pet_age,
    onlyBreeders: search.only_breeders,
    maxPrice: search.max_price === 50000 ? null : search.max_price,
    maxDistanceInKm: search.max_distance_in_km,
    latitude: search.latitude ?? null,
    longitude: search.longitude ?? null,
    notify: search.notify,
  };
}

export function loadSavedSearchPageData(
  req: RequestClient,
  params: { search: string; page?: number | string },
) {
  return req<{ search: Search; listings: Paginator<Listing> }>(
    apiRoute('searches.show', { search: params.search }),
    { page: params.page },
  );
}
